package com.example.mystybag.common.extensions

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FiniteAnimationSpec
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.IndicationNodeFactory
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.interaction.InteractionSource
import androidx.compose.foundation.interaction.PressInteraction
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.ime
import androidx.compose.foundation.layout.navigationBars
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.LocalRippleConfiguration
import androidx.compose.material3.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.drawscope.ContentDrawScope
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.layout
import androidx.compose.ui.node.CompositionLocalConsumerModifierNode
import androidx.compose.ui.node.DelegatableNode
import androidx.compose.ui.node.DrawModifierNode
import androidx.compose.ui.node.currentValueOf
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.stack.animation.StackAnimator
import com.arkivanov.decompose.extensions.compose.stack.animation.stackAnimator
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.common.ui.components.custom.ItemDecor
import com.example.mystybag.core.ui.theme.LocalTheme.p0
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.views.AppTheme
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock

@Suppress("VARIABLE_IN_SINGLETON_WITHOUT_THREAD_LOCAL")
object ClickTracker {
    private var lastClickEvent = 0L
    private const val CLICK_INTERVAL = 300L

    fun tryClick(identifier: String? = null, action: (() -> Unit)?) {
        action?.also { onClick ->
            val current = Clock.System.now().toEpochMilliseconds()
            if ((current - lastClickEvent) > CLICK_INTERVAL) {
                lastClickEvent = current
                identifier?.also {
                    LogHelper.logEvent("Button click -> $identifier")
                }
                onClick()
            }
        }
    }
}

fun Modifier.modifyIf(predicate: Boolean, block: Modifier.() -> Modifier): Modifier =
    if (predicate)
        block(this)
    else
        this

@Composable
fun Modifier.shimmer(
    show            : Boolean,
    isCircleShape   : Boolean   = false,
    color           : Color     = AppTheme.current.colors.onSurfaceVariant,
    width           : Float     = 1f,
    cornerRadius    : Float     = 100f
): Modifier {
    return if (show) {
        val shimmerColors       = remember { listOf(
            color.copy(alpha = 0.2f),
            color.copy(alpha = 0.4f),
            color.copy(alpha = 0.2f),
        ) }
        val transition          = rememberInfiniteTransition()
        val translateAnimation  = transition.animateFloat(
            initialValue    = 0f,
            targetValue     = 800f,
            animationSpec   = infiniteRepeatable(
                animation       = tween(1200),
                repeatMode      = RepeatMode.Reverse
            )
        )
        val brush               = Brush.linearGradient(
            colors  = shimmerColors,
            start   = Offset.Zero,
            end     = Offset(x = translateAnimation.value, y = translateAnimation.value)
        )

        this
            .fillMaxWidth(width)
            .drawWithContent {
                if (isCircleShape)
                   drawCircle(brush = brush)
                else
                    drawRoundRect(brush = brush, cornerRadius = CornerRadius(cornerRadius))
            }
            .wrapContentHeight()
    }
    else
        this
}

fun Modifier.noRippleClickable(action: (()->Unit)?): Modifier =
    action?.let {
        this.clickable(
            indication          = null,
            interactionSource   = null,
            onClick             = { ClickTracker.tryClick(null, it) }
        )
    } ?: this

fun Modifier.doOnClick(action: (() -> Unit)?) =
    action?.let {
        this.clickable(
            interactionSource   = null,
            indication          = ripple(bounded = true),
            onClick             = { ClickTracker.tryClick(null, it) }
        )
    } ?: this

fun Modifier.onClick(action: (() -> Unit)?) =
    action?.let { onClick ->
        this.clickable(
            interactionSource   = null,
            indication          = ripple(bounded = true),
            onClick             = onClick
        )
    } ?: this

fun none(animationSpec: FiniteAnimationSpec<Float> = tween()): StackAnimator =
    stackAnimator(animationSpec = animationSpec) { _, _, content ->
        content(Modifier)
    }

fun slideUP(animationSpec: FiniteAnimationSpec<Float> = tween()): StackAnimator =
    stackAnimator(animationSpec = animationSpec) { factor, _, content ->
        content(Modifier.offsetYFactor(factor = factor))
    }

fun Modifier.clipCorners(itemDecor: ItemDecor?, topPadding: Dp = p0, bottomPadding: Dp = p0): Modifier {
    val top         = if (itemDecor?.roundedTop == true)    p16 else p0
    val bottom      = if (itemDecor?.roundedBottom == true) p16 else p0
    val topPad      = if (itemDecor?.roundedTop == true)    topPadding else p0
    val bottomPad   = if (itemDecor?.roundedBottom == true) bottomPadding else p0
    return this
        .padding(top = topPad, bottom = bottomPad)
        .clip(RoundedCornerShape(topStart = top, topEnd = top, bottomStart = bottom, bottomEnd = bottom))
}

@Composable
fun Modifier.keyboardPadding(): Modifier = this.padding(bottom = (WindowInsets.ime.asPaddingValues().calculateBottomPadding() - WindowInsets.navigationBars.asPaddingValues().calculateBottomPadding()).coerceAtLeast(p0))

@Composable
fun Modifier.scrollEdgeFade(
    edgeSize: Dp = 24.dp,
    overlayColor: Color = AppTheme.current.colors.background,
    showTop: Boolean = true,
    showBottom: Boolean = true
): Modifier {
    val edgeSizePx = with(LocalDensity.current) { edgeSize.toPx() }
    if (edgeSizePx <= 0f || (!showTop && !showBottom)) return this

    return this.drawWithContent {
        drawContent()

        val fadeHeight = edgeSizePx.coerceAtMost(size.height / 2f)
        if (fadeHeight <= 0f) return@drawWithContent

        if (showTop) {
            drawRect(
                brush = Brush.verticalGradient(
                    colors = listOf(overlayColor, Color.Transparent),
                    startY = 0f,
                    endY = fadeHeight
                ),
                topLeft = Offset(0f, 0f),
                size = Size(size.width, fadeHeight)
            )
        }

        if (showBottom) {
            drawRect(
                brush = Brush.verticalGradient(
                    colors = listOf(Color.Transparent, overlayColor),
                    startY = size.height - fadeHeight,
                    endY = size.height
                ),
                topLeft = Offset(0f, size.height - fadeHeight),
                size = Size(size.width, fadeHeight)
            )
        }
    }
}

private fun Modifier.offsetYFactor(factor: Float): Modifier =
    layout { measurable, constraints ->
        val placeable = measurable.measure(constraints)

        layout(placeable.width, placeable.height) {
            placeable.placeRelative(x = 0, y = (placeable.height.toFloat() * factor).toInt())
        }
    }

fun Modifier.roundedContainer(padding: Dp = p4, background: Color? = null, roundPadding: Dp = p16): Modifier =
    this.clip(RoundedCornerShape(roundPadding))
        .surfaceBackground(background)
        .padding(padding)


fun Modifier.rippleClick(padding: Dp = p12, background: Color? = null, roundPadding: Dp = p12, action: () -> Unit): Modifier {
    return this
        .clip(RoundedCornerShape(roundPadding))
        .modifyIf(background != null) {
            background(background!!)
        }
        .doOnClick(action)
        .padding(padding)
}

fun Modifier.rippleClick(padding: Dp = p12, background: Color? = null, roundPadding: Dp = p12, action: () -> Unit, longAction: (() -> Unit)? = null): Modifier {
    return this
        .clip(RoundedCornerShape(roundPadding))
        .modifyIf(background != null) {
            background(background!!)
        }
        .combinedClickable (
            onClick         = { ClickTracker.tryClick(null, action) },
            onLongClick     = longAction?.let { { ClickTracker.tryClick(null, it) } }
        )
        .padding(padding)
}

fun Modifier.onLongClick(callback: () -> Unit): Modifier {
    return this.pointerInput(Unit) {
        detectTapGestures(
            onLongPress = {
                callback()
            }
        )
    }
}

@Composable
fun Modifier.verticalScrollbar(state: ScrollState, color: Color = AppTheme.current.colors.primary): Modifier {
    return this then Modifier.drawWithContent {
        drawContent()

        val viewHeight      = state.viewportSize.toFloat()
        val contentHeight   = state.maxValue + viewHeight

        val scrollbarHeight     = (viewHeight * (viewHeight / contentHeight )).coerceIn(p4.toPx() .. viewHeight)
        val variableZone        = viewHeight - scrollbarHeight
        val scrollbarYoffset    = (state.value.toFloat() / state.maxValue) * variableZone

        drawRoundRect(
            cornerRadius    = CornerRadius(p4.toPx() / 2, p4.toPx() / 2),
            color           = color,
            topLeft         = Offset(this.size.width - p12.toPx(), scrollbarYoffset + p12.toPx()),
            size            = Size(p4.toPx(), scrollbarHeight - p24.toPx())
        )
    }
}

object CustomIndicationNodeFactory: IndicationNodeFactory {
    override fun create(interactionSource: InteractionSource): DelegatableNode = CustomIndicationNode(interactionSource)
    override fun hashCode(): Int = -1
    override fun equals(other: Any?) = other === this
}

@OptIn(ExperimentalMaterial3Api::class)
private class CustomIndicationNode(private val interactionSource: InteractionSource): Modifier.Node(), DrawModifierNode, CompositionLocalConsumerModifierNode {

    private var currentPressPosition = Offset.Zero
    private val animatedAlpha        = Animatable(0f)
    private var isShowing            = false
    private var radius               = CornerRadius.Zero
    private var pressedAlpha         = 1f
    private var color                = Color.Unspecified
    private var enabled              = false

    private suspend fun animateToPressed(pressPosition: Offset) {
        currentPressPosition = pressPosition
        isShowing = true
        animatedAlpha.animateTo(1f, tween(150))
    }

    private suspend fun animateToResting() {
        isShowing = false
        animatedAlpha.animateTo(0f, tween(250))
    }

    override fun onAttach() {
        radius = with(currentValueOf(LocalDensity)) {
            p16.toPx().let {
                CornerRadius(it, it)
            }
        }

        with (currentValueOf(LocalRippleConfiguration)) {
            enabled      = this != null
            pressedAlpha = this?.rippleAlpha?.pressedAlpha ?: 1f
            color        = this?.color?.copy(alpha = pressedAlpha) ?: Color.Unspecified
        }

        coroutineScope.launch {
            interactionSource.interactions.collect { interaction ->
                when (interaction) {
                    is PressInteraction.Press -> animateToPressed(interaction.pressPosition)
                    is PressInteraction.Release,
                    is PressInteraction.Cancel -> animateToResting()
                }
            }
        }
    }

    override fun ContentDrawScope.draw() {
        if (enabled) {
            drawRoundRect(
                brush = SolidColor(if (isShowing) color else color.copy (alpha = pressedAlpha * animatedAlpha.value)),
                size = Size(
                    width = if (isShowing) this.size.width * animatedAlpha.value else this.size.width,
                    height = this.size.height
                ),
                topLeft = Offset(
                    x = if (isShowing) (currentPressPosition.x * (1f - animatedAlpha.value)) else 0f,
                    y = 0f
                ),
                cornerRadius = radius
            )
        }

        drawContent()
    }
}